import {
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';

import {
  markerBox,
  mitFarbe,
  mitGroesse,
  overlayInnerSvg,
  skaliereMarker,
  verschiebeMarker,
  type Marker,
  type Punkt,
  type SizeStep,
} from '../../domain/annotation';
import {
  ausJson,
  bloeckeBei,
  FASSUNG,
  type Block,
  type VideoOverlay,
} from '../../domain/videoAnnotation';
import { useT } from '../../i18n';
import { appendAppLog } from '../../platform/appLog';
import { abspielAdresse } from '../../platform/medienAdresse';
import { annotationsPfad, readBase64, readText } from '../../platform/storage';
import { thumbPfad } from '../../platform/video';
import { FRAME_ANZAHL, frameOrdner, frameZeiten, VideoFrames } from '../../platform/videoFrames';
import { useAppStore } from '../../store/useAppStore';
import { Icon } from '../../ui/Icon';
import { FARBEN, Toolbar, type Werkzeug } from '../annotate/Toolbar';
import { Blockleiste } from './Blockleiste';
import { bandVon, sichtbareMarken } from './sicht';
import { startZustand, videoReducer } from './reducer';
import { sichereVideoEbene } from './videoAnnotateSave';
import styles from './VideoAnnotateSheet.module.css';
import { Zeitleiste } from './Zeitleiste';

/**
 * Eine Videoaufnahme in Grossansicht, mit ihrer zeitlich begrenzten Markierungsebene.
 *
 * **Zwei Ebenen, ein Massstab** — wie beim Foto: das `<video>` und das `<svg>` liegen
 * deckungsgleich in einer Bühne, alle Marken stehen in Videopixeln, die `viewBox` trägt die
 * Videogrösse. Dreht der Prüfer das iPad, skaliert der Browser beides gemeinsam.
 *
 * **Gezeichnet wird mit demselben Markup, das der Server später auszuwerten hat** — die Daten
 * stehen im JSON, `overlayInnerSvg` macht daraus das Bild. Eine zweite Zeichenroutine für den
 * Bildschirm wäre eine zweite Quelle der Wahrheit.
 */

const neueId = () => Math.random().toString(36).slice(2, 10);

/** Wie lang ein Block wird, den das Zeichnen von selbst anlegt. */
const VORGABE_BLOCK_MS = 3000;

/**
 * Wie weit der Anstoss nach dem Laden springt, damit WebKit einen Frame dekodiert.
 *
 * Gross genug, um sicher in ein anderes Einzelbild zu fallen (25 fps sind 40 ms), klein genug,
 * dass niemand den Versatz als „das Video fängt nicht am Anfang an" bemerkt.
 */
const ANSTOSS_MS = 50;

const abbild = (o: VideoOverlay): VideoOverlay => JSON.parse(JSON.stringify(o)) as VideoOverlay;

/** Woran gerade gezogen wird — auf der Zeichenebene, nicht in der Leiste. */
type Zug =
  | { art: 'zeichnen'; start: Punkt; marke: Marker }
  | { art: 'verschieben'; letzter: Punkt; id: string }
  | { art: 'skalieren'; anker: Punkt; start: Punkt; id: string; marke: Marker };

interface Props {
  /** Pfad der Videoaufnahme unterhalb von Documents. */
  path: string;
  taskId: string;
  onClose: () => void;
  /** Meldet einen fehlgeschlagenen Schreibvorgang, **mit** dem Fehler — s. `AnnotateSheet`. */
  onSaveFailed?: (fehler: unknown) => void;
}

export function VideoAnnotateSheet({ path, taskId, onClose, onSaveFailed }: Props) {
  const t = useT();
  const video = useRef<HTMLVideoElement>(null);
  const zug = useRef<Zug | null>(null);
  /** Der Stand beim Öffnen — die Vergleichsgrundlage für `sichereVideoEbene`. */
  const geladen = useRef<VideoOverlay | null>(null);

  const [adresse, setAdresse] = useState<string | null>(null);
  /**
   * Das Standbild, das steht, bevor WebKit einen Frame dekodiert hat.
   *
   * ⚠️ **Befund des Nutzers vom 2026-09-22.** Die Bühne blieb beim Öffnen leer und füllte sich
   * erst nach dem ersten Antippen von „Abspielen". Der Grund liegt nicht an der Adresse: iOS
   * malt bei `preload="metadata"` kein Bild, es holt nur Masse und Dauer. Dieselbe Datei, die
   * die Kachel in der Übersicht zeigt (`<video>-thumb.jpg`), steht hier als `poster` — sie
   * liegt schon auf der Platte und ist in einem Zug gelesen.
   *
   * **Als Datenadresse, nicht über `convertFileSrc`.** Ein JPEG von wenigen Kilobyte kostet
   * das nichts, und es geht denselben Weg wie die Kachel, der am Gerät bewiesen ist.
   */
  const [poster, setPoster] = useState<string | null>(null);
  /**
   * Warum die Aufnahme nicht zu sehen ist — oder `null`, solange nichts schiefging.
   *
   * ⚠️ **Eine leere Bühne ist keine Meldung.** Bis zum 2026-09-21 fing der Editor jeden Fehler
   * beim Bilden der Adresse mit `.catch(() => null)` ab und zeigte dann gar nichts: kein
   * Video, keine Ebene, kein Grund. Der Prüfer stünde damit vor einem Blatt, das nichts tut.
   */
  const [fehler, setFehler] = useState<string | null>(null);
  const [bilder, setBilder] = useState<string[]>([]);
  const [bereit, setBereit] = useState(false);
  /**
   * Ist der Ton eingeschaltet? Beim Öffnen **nein** (Vorgabe des Nutzers, 2026-09-21).
   *
   * ⚠️ **Nicht gemerkt, auch nicht für die Sitzung.** Jede geöffnete Aufnahme beginnt stumm:
   * im Prüfungsraum ist das die sichere Seite, und eine Aufnahme, die unerwartet laut
   * losspielt, stört die Station nebenan.
   */
  const [ton, setTon] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [positionMs, setPositionMs] = useState(0);
  const [offen, setOffen] = useState(true);

  const [werkzeug, setWerkzeug] = useState<Werkzeug>('arrow');
  const [farbe, setFarbe] = useState(FARBEN[0]);
  const [stufe, setStufe] = useState<SizeStep>('mittel');
  const [vorschau, setVorschau] = useState<Marker | null>(null);
  const [textFrage, setTextFrage] = useState<{ x: number; y: number } | null>(null);
  const [textEingabe, setTextEingabe] = useState('');

  const [state, dispatch] = useReducer(
    videoReducer,
    startZustand({ version: FASSUNG, width: 0, height: 0, durationMs: 0, blocks: [] }),
  );

  /*
   * Solange dieses Blatt offen ist, schwebt kein Schlafknopf darüber.
   *
   * ⚠️ Er stünde mit `z-index: 61` über dem Blatt und verdeckte rechts unten die Zeitleiste —
   * genau dort liegt das Videoende (`schlaf/schlafecke.ts::zeigtSchwebendenKnopf`). Meldung
   * nach aussen, kein Betriebsmittel: dasselbe Muster wie `medienAufnahme`.
   */
  useEffect(() => {
    const { setVollbildEditor } = useAppStore.getState();
    setVollbildEditor(true);
    return () => setVollbildEditor(false);
  }, []);

  /* Laden: die Adresse des Videos und die vorhandene Ebene. */
  useEffect(() => {
    let lebt = true;
    void (async () => {
      let grund: string | null = null;
      const [quelle, text, standbild] = await Promise.all([
        abspielAdresse(path).catch((f: unknown) => {
          // Der Grund wird gezeigt, nicht verschluckt; ins Dateilog hat ihn `abspielAdresse`
          // schon geschrieben.
          grund = f instanceof Error ? f.message : String(f);
          return null;
        }),
        readText(annotationsPfad(path)).catch(() => null),
        // Fehlt das Standbild, bleibt die Bühne einen Augenblick leer — das ist der alte
        // Zustand, kein neuer Fehler. Deshalb wird hier nichts gemeldet.
        readBase64(thumbPfad(path)).catch(() => null),
      ]);
      if (!lebt) return;
      setAdresse(quelle);
      setFehler(grund);
      if (standbild) setPoster('data:image/jpeg;base64,' + standbild);
      if (text) {
        try {
          const ebene = ausJson(text);
          geladen.current = abbild(ebene);
          dispatch({ typ: 'ersetzen', overlay: ebene });
        } catch (fehler) {
          /*
           * ⚠️ **Eine unlesbare Ebene wird nicht überschrieben, sondern stehen gelassen.** Der
           * Editor startet leer, und weil `geladen` dann `null` bleibt, schreibt das Schliessen
           * nur, wenn der Prüfer wirklich etwas gezeichnet hat. Die alte Datei ist notfalls
           * noch von Hand zu retten.
           *
           * ⛔ **Das ist kein fehlgeschlagenes Schreiben** und ging bis zum 2026-09-24 trotzdem
           * als solches hinaus (`onSaveFailed`). Hier ist nichts verlorengegangen — es wurde nur
           * nichts gelesen; die Meldung gehört ins Dateilog, nicht vor den Prüfer
           * ([[keine-fehler-im-normalbetrieb]]).
           */
          void appendAppLog('Video-Ebene nicht lesbar, Editor startet leer: ' + String(fehler), 'warn');
        }
      }
    })();
    return () => {
      lebt = false;
    };
  }, [path, onSaveFailed]);

  /*
   * Der Tonzustand gehört ans Element, nicht nur ins Markup: React schreibt `muted` nicht
   * zuverlässig ins HTML, und ohne diese Zeile startet iOS mit Ton.
   */
  useEffect(() => {
    const el = video.current;
    if (el) el.muted = !ton;
  }, [ton, adresse]);

  /*
   * Der Takt der Wiedergabe.
   *
   * ⚠️ **`requestAnimationFrame`, nicht `timeupdate`.** Letzteres feuert nur etwa alle 250 ms;
   * eine Marke bliebe damit nach dem Ende ihres Blocks sichtbar nachhängen, und genau das
   * Ein- und Ausblenden ist der Zweck der ganzen Übung.
   */
  useEffect(() => {
    if (!laeuft) return;
    let angefordert = 0;
    const takt = () => {
      const el = video.current;
      if (el) setPositionMs(Math.round(el.currentTime * 1000));
      angefordert = requestAnimationFrame(takt);
    };
    angefordert = requestAnimationFrame(takt);
    return () => cancelAnimationFrame(angefordert);
  }, [laeuft]);

  const masseStehen = useCallback(() => {
    const el = video.current;
    if (!el) return;
    /*
     * ⚠️ **`videoWidth`/`videoHeight` kommen bereits gedreht.** WebKit wendet die
     * `preferredTransform` an, die `VideoSegmentMerger` in die Datei schreibt; die Masse sind
     * also die **angezeigten**. Würde hier die rohe Spurgrösse stehen, lägen die Marken einer
     * quer aufgenommenen Szene um 90 Grad verdreht.
     */
    const overlay: VideoOverlay = {
      version: FASSUNG,
      width: el.videoWidth,
      height: el.videoHeight,
      durationMs: Math.round(el.duration * 1000),
      blocks: geladen.current?.blocks ?? [],
    };
    dispatch({ typ: 'ersetzen', overlay });
    geladen.current = geladen.current ? overlay : null;
    setBereit(true);

    /*
     * ⚠️ **Der Anstoss.** Bis hierher hat WebKit Masse und Dauer, aber kein Bild gemalt: auf
     * iOS bleibt die Bühne nach `loadedmetadata` schwarz, bis einmal wirklich dekodiert wird.
     * Ein Sprung an eine Stelle **ungleich** der aktuellen erzwingt das — `currentTime = 0`
     * bei `currentTime === 0` tut dagegen gar nichts.
     *
     * Die 50 ms gehen auch in `positionMs`: das Bild zeigt dann diesen Augenblick, und die
     * Marken, die die Ebene dazu einblendet, müssen zu ihm gehören und nicht zu 0.
     */
    if (el.currentTime === 0 && overlay.durationMs > ANSTOSS_MS) {
      el.currentTime = ANSTOSS_MS / 1000;
      setPositionMs(ANSTOSS_MS);
    }

    void VideoFrames.extract({
      path,
      ordner: frameOrdner(path),
      msList: frameZeiten(overlay.durationMs, FRAME_ANZAHL),
      breite: 160,
    })
      .then(async ({ pfade }) => {
        const adressen = await Promise.all(pfade.map((p) => abspielAdresse(p).catch(() => null)));
        setBilder(adressen.map((a) => a ?? ''));
      })
      // Fehlt der Streifen, bleibt die Leiste leer und trotzdem bedienbar: sie ist eine
      // Ahnung, keine Bedingung.
      .catch(() => setBilder([]));
  }, [path]);

  const springe = useCallback((ms: number) => {
    const el = video.current;
    if (!el) return;
    el.currentTime = ms / 1000;
    setPositionMs(ms);
  }, []);

  const anhalten = useCallback(() => {
    video.current?.pause();
    setLaeuft(false);
  }, []);

  const umschalten = () => {
    const el = video.current;
    if (!el) return;
    if (laeuft) {
      anhalten();
      return;
    }
    void el.play();
    setLaeuft(true);
  };

  /**
   * Ein neues Band an der Strichposition — **immer neu**, auch wenn dort schon eines gilt.
   *
   * `spurFuer` legt es dann in die nächste freie Zeile. Es ist zunächst leer; bliebe es so,
   * wirft `leere-verwerfen` es beim Schliessen wieder weg.
   */
  const neuesBand = () => {
    anhalten();
    const toMs = Math.min(positionMs + VORGABE_BLOCK_MS, state.overlay.durationMs);
    // Am Videoende wäre das Band sonst kürzer als die Mindestlänge oder gar leer.
    const fromMs = Math.max(0, Math.min(positionMs, state.overlay.durationMs - VORGABE_BLOCK_MS));
    dispatch({ typ: 'block-hinzu', id: neueId(), fromMs, toMs: Math.max(toMs, fromMs + 1) });
  };

  /**
   * Ein Band auswählen — und, wenn seine Zeit gerade nicht gilt, an seinen Anfang springen.
   *
   * ⚠️ **Ohne den Sprung wäre das Band unerreichbar.** Seit die Zeit allein über die Sicht
   * entscheidet (`sicht.ts`), zeigt ein gewähltes Band ausserhalb seiner Zeit nichts — man
   * tippte es in der Leiste an und sähe eine leere Bühne.
   *
   * Gesprungen wird auf `fromMs`, nicht in die Mitte: dort beginnt, was der Prüfer gemeint
   * hat, und von dort aus lässt sich abspielen.
   */
  const waehleBand = (id: string | null) => {
    dispatch({ typ: 'block-waehlen', id });
    if (id === null) return;
    const band = state.overlay.blocks.find((b) => b.id === id);
    if (band && (positionMs < band.fromMs || positionMs >= band.toMs)) {
      anhalten();
      springe(band.fromMs);
    }
  };

  const schliessen = () => {
    anhalten();
    // Erst räumen, dann vergleichen: ein Block ohne Marken ist nichts, was jemand behalten
    // wollte, und stünde sonst in der Datei.
    const geraeumt = videoReducer(state, { typ: 'leere-verwerfen' });
    void sichereVideoEbene(path, taskId, geladen.current, geraeumt.overlay)
      .catch((fehler: unknown) => onSaveFailed?.(fehler))
      .finally(onClose);
  };

  const { fremd, eigen } = sichtbareMarken(state.overlay.blocks, positionMs, state.auswahl);
  /** Alles, was gerade anfassbar ist — die Reihenfolge ist zugleich die Stapelfolge. */
  const marken = [...fremd, ...eigen];
  const ausgewaehlt = marken.find((m) => m.id === state.markeAuswahl) ?? null;
  /** Das Band, in das eine neue Marke gehört — `undefined`, wenn hier gerade keines gilt. */
  const aktiv: Block | undefined = bloeckeBei(state.overlay.blocks, positionMs).slice(-1)[0];

  /* ---- Zeichnen auf der Ebene ---- */

  const zuVideo = (e: ReactPointerEvent<SVGSVGElement>): Punkt => {
    const r = e.currentTarget.getBoundingClientRect();
    return {
      x: ((e.clientX - r.left) / r.width) * state.overlay.width,
      y: ((e.clientY - r.top) / r.height) * state.overlay.height,
    };
  };

  const neueMarke = (p: Punkt): Marker | null => {
    const id = neueId();
    switch (werkzeug) {
      case 'arrow':
        return { id, kind: 'arrow', color: farbe, stroke: stufe, von: p, bis: p };
      case 'ellipse':
        return { id, kind: 'ellipse', color: farbe, stroke: stufe, cx: p.x, cy: p.y, rx: 0, ry: 0 };
      case 'rect':
        return { id, kind: 'rect', color: farbe, stroke: stufe, x: p.x, y: p.y, w: 0, h: 0 };
      case 'cover':
        return { id, kind: 'cover', color: farbe, x: p.x, y: p.y, w: 0, h: 0 };
      case 'freehand':
        return { id, kind: 'freehand', color: farbe, stroke: stufe, punkte: [p] };
      default:
        return null;
    }
  };

  /**
   * Sorgt dafür, dass es einen Block gibt, in den die Marke gehört.
   *
   * ⚠️ **Ohne Block hätte die Marke keine Zeit** — der Reducer lässt sie dann fallen. Statt den
   * Prüfer erst einen Block anlegen zu lassen, entsteht er hier von selbst an der Stelle, an
   * der die Wiedergabe steht.
   */
  const stelleBlockSicher = (): void => {
    /*
     * ⚠️ **Die Auswahl gilt nur, solange ihr Band auch zeitlich gilt.** Seit die Zeit allein
     * über die Sicht entscheidet (`sicht.ts`), wäre eine Marke, die in ein gewähltes, aber
     * gerade nicht geltendes Band fiele, im selben Augenblick unsichtbar — der Prüfer sähe
     * sein eigenes Zeichnen verschwinden.
     */
    const gewaehlt = state.overlay.blocks.find((b) => b.id === state.auswahl);
    if (gewaehlt && positionMs >= gewaehlt.fromMs && positionMs < gewaehlt.toMs) return;

    if (aktiv) {
      dispatch({ typ: 'block-waehlen', id: aktiv.id });
      return;
    }
    const toMs = Math.min(positionMs + VORGABE_BLOCK_MS, state.overlay.durationMs);
    dispatch({ typ: 'block-hinzu', id: neueId(), fromMs: positionMs, toMs });
  };

  const down = (e: ReactPointerEvent<SVGSVGElement>) => {
    if (!bereit) return;
    /*
     * Eine offene Textfrage fängt den ersten Tipper ab: sie gehört zu einer Stelle im Bild,
     * und ein Tipp woanders sagt, dass diese Stelle nicht mehr gemeint ist. Ohne das zeichnete
     * der Prüfer unter einem Feld weiter, das noch auf seine Eingabe wartete.
     */
    if (textFrage) {
      textAbbrechen();
      return;
    }
    e.currentTarget.setPointerCapture(e.pointerId);
    anhalten();
    const p = zuVideo(e);

    const getroffen = (e.target as Element).closest?.('[data-id]')?.getAttribute('data-id');
    if (getroffen) {
      /*
       * ⚠️ **Erst das Band, dann die Marke.** `block-waehlen` raeumt die Markenauswahl weg
       * (`reducer.ts`) — umgekehrt bliebe von der Auswahl nichts uebrig. Ein Tipp auf eine
       * fremde Marke holt so ihr Band nach vorn, statt ins Leere zu laufen.
       */
      const band = bandVon(state.overlay.blocks, getroffen);
      if (band && band !== state.auswahl) dispatch({ typ: 'block-waehlen', id: band });
      // Kein Sprung: die Marke liegt unter dem Finger, ihr Band gilt also ohnehin gerade.
      dispatch({ typ: 'marke-waehlen', id: getroffen });
      dispatch({ typ: 'zug-start' });
      zug.current = { art: 'verschieben', letzter: p, id: getroffen };
      return;
    }

    if (werkzeug === 'text' || werkzeug === 'number') {
      stelleBlockSicher();
      if (werkzeug === 'text') {
        setTextEingabe('');
        setTextFrage(p);
        return;
      }
      dispatch({
        typ: 'marke-hinzu',
        marke: { id: neueId(), kind: 'number', color: farbe, size: stufe, x: p.x, y: p.y, n: 1 },
      });
      return;
    }

    const marke = neueMarke(p);
    if (!marke) return;
    zug.current = { art: 'zeichnen', start: p, marke };
    setVorschau(marke);
  };

  const move = (e: ReactPointerEvent<SVGSVGElement>) => {
    const laufend = zug.current;
    if (!laufend) return;
    const p = zuVideo(e);

    if (laufend.art === 'verschieben') {
      const marke = marken.find((m) => m.id === laufend.id);
      if (!marke) return;
      dispatch({
        typ: 'marke-aendern',
        marke: verschiebeMarker(marke, p.x - laufend.letzter.x, p.y - laufend.letzter.y),
      });
      zug.current = { ...laufend, letzter: p };
      return;
    }

    if (laufend.art === 'skalieren') {
      // Das Verhältnis zum Ankerpunkt bestimmt den Faktor. Sehr kleine Abstände ergäben
      // riesige Faktoren, deshalb der Mindestabstand — wie im Foto-Editor.
      const breite = Math.max(Math.abs(laufend.start.x - laufend.anker.x), w * 0.01);
      const hoehe = Math.max(Math.abs(laufend.start.y - laufend.anker.y), h * 0.01);
      dispatch({
        typ: 'marke-aendern',
        marke: skaliereMarker(
          laufend.marke,
          Math.abs(p.x - laufend.anker.x) / breite,
          Math.abs(p.y - laufend.anker.y) / hoehe,
          laufend.anker,
        ),
      });
      return;
    }

    const gezogen = ziehe(laufend.marke, laufend.start, p);
    zug.current = { ...laufend, marke: gezogen };
    setVorschau(gezogen);
  };

  const up = (e: ReactPointerEvent<SVGSVGElement>) => {
    const laufend = zug.current;
    zug.current = null;
    e.currentTarget.releasePointerCapture(e.pointerId);

    if (!laufend) return;
    if (laufend.art === 'verschieben' || laufend.art === 'skalieren') {
      dispatch({ typ: 'zug-ende' });
      return;
    }

    setVorschau(null);
    // Ein blosses Antippen ohne Ziehen hinterlässt eine Marke ohne Ausdehnung — sie wäre
    // unsichtbar und liesse sich nie wieder anfassen.
    const box = markerBox(laufend.marke, state.overlay.height);
    if (box.w < 2 && box.h < 2 && laufend.marke.kind !== 'freehand') return;

    stelleBlockSicher();
    dispatch({ typ: 'marke-hinzu', marke: laufend.marke });
  };

  /*
   * ⚠️ **Die Frage muss auch ohne Antwort weggehen.** Ohne Abbruch blieb das Feld stehen, bis
   * der Prüfer etwas eintippte — auch dann, wenn er längst ein anderes Werkzeug gewählt hatte
   * (Befund des Nutzers, 2026-09-20).
   */
  const textAbbrechen = () => {
    setTextFrage(null);
    setTextEingabe('');
  };

  const textSetzen = () => {
    const stelle = textFrage;
    setTextFrage(null);
    if (!stelle || textEingabe.trim() === '') return;
    dispatch({
      typ: 'marke-hinzu',
      marke: {
        id: neueId(),
        kind: 'text',
        color: farbe,
        size: stufe,
        x: stelle.x,
        y: stelle.y,
        text: textEingabe.trim(),
      },
    });
  };

  const { width: w, height: h } = state.overlay;
  const rahmen = ausgewaehlt ? markerBox(ausgewaehlt, h) : null;
  /** Der Anfasser ist gross genug für eine Fingerkuppe, aber klein genug für eine kurze Marke. */
  const griff = Math.max(w, h) * 0.018;

  return (
    <div className={styles.blatt} role="dialog" aria-modal="true">
      <div className={styles.raum}>
        {/*
          ⚠️ **Der Grund steht sichtbar auf dem Blatt**, samt Meldung des Systems. Sie ist
          englisch und technisch — aber sie ist das, was am Prüfungstag weiterhilft, und sie
          steht zusätzlich im Dateilog (`platform/medienAdresse.ts`).
        */}
        {fehler && (
          <div className={styles.fehler} role="status">
            <span>{t.videoAnnotate.notPlayable}</span>
            <code>{fehler}</code>
          </div>
        )}
        {adresse && (
          <div className={styles.buehne} style={{ aspectRatio: w && h ? `${w} / ${h}` : undefined }}>
            {/*
              ⚠️ **`playsInline` ist Pflicht und `controls` muss weg.** Ohne beides reisst iOS
              das Video in den nativen Vollbildspieler — die Markierungsebene läge dahinter und
              wäre weder zu sehen noch zu bedienen.
            */}
            <video
              ref={video}
              className={styles.video}
              src={adresse}
              playsInline
              /*
               * ⚠️ **Drei Dinge gegen die leere Bühne** (Befund des Nutzers, 2026-09-22), und
               * jedes greift für sich:
               *
               * - `poster` — das Standbild liegt schon auf der Platte und steht sofort da,
               *   noch bevor WebKit die Aufnahme angefasst hat.
               * - `preload="auto"` statt `metadata` — `metadata` holt nur Masse und Dauer;
               *   iOS dekodiert dabei **keinen** Frame und malt folglich nichts.
               * - der Anstoss in `masseStehen` — ein Sprung an eine Stelle ungleich 0 zwingt
               *   WebKit, wirklich einen Frame zu dekodieren und anzuzeigen.
               */
              poster={poster ?? undefined}
              preload="auto"
              muted
              onLoadedMetadata={masseStehen}
              onEnded={() => setLaeuft(false)}
              /*
               * ⚠️ **Auch eine gebildete Adresse kann ins Leere zeigen.** `getUri` gelingt,
               * und WebKit holt unter `capacitor://localhost/_capacitor_file_/…` trotzdem
               * nichts — dann käme kein `loadedmetadata`, die Ebene bliebe aus, und ohne
               * diesen Horcher sähe der Prüfer wieder nur ein Blatt, das nichts tut.
               */
              onError={() => {
                const nummer = video.current?.error?.code;
                const text =
                  'WebKit konnte die Aufnahme nicht laden' +
                  (nummer ? ' (MediaError ' + String(nummer) + ')' : '');
                setFehler(text);
                void appendAppLog('Video im Editor: ' + path + ' — ' + text, 'warn').catch(
                  () => undefined,
                );
              }}
            />
            {bereit && (
              <svg
                className={styles.ebene}
                viewBox={`0 0 ${w} ${h}`}
                onPointerDown={down}
                onPointerMove={move}
                onPointerUp={up}
                onPointerCancel={up}
              >
                {/* Dasselbe Markup, aus dem der Server später sein Bild baut. Der Text darin
                    ist bereits maskiert (`esc` in `domain/annotation.ts`). */}
                {/*
                  ⚠️ **Zwei Gruppen, nicht eine.** Die Marken der übrigen gerade geltenden
                  Bänder stehen blass darunter: bei überschneidenden Bändern weiss man sonst
                  nicht, welche Marke man gerade anfasst (Entscheidung des Nutzers,
                  2026-09-20). Anfassbar bleiben sie trotzdem — ein Tipp darauf holt ihr Band
                  nach vorn.
                */}
                <g
                  opacity={0.4}
                  dangerouslySetInnerHTML={{
                    __html: overlayInnerSvg({ width: w, height: h, markers: fremd }),
                  }}
                />
                <g
                  dangerouslySetInnerHTML={{
                    __html: overlayInnerSvg({
                      width: w,
                      height: h,
                      markers: vorschau ? [...eigen, vorschau] : eigen,
                    }),
                  }}
                />

                {/*
                  ⚠️ **Die Auswahl muss zu sehen sein.** Ohne Rahmen liess sich eine Marke zwar
                  antippen, aber nichts sagte es — der Prüfer hielt das Auswählen für kaputt
                  (Befund des Nutzers, 2026-09-20). Gestrichelt und ohne Füllung, damit es mit
                  nichts zu verwechseln ist, was der Prüfer selbst gezeichnet hat.
                */}
                {rahmen && (
                  <g pointerEvents="none">
                    <rect
                      x={rahmen.x - griff}
                      y={rahmen.y - griff}
                      width={rahmen.w + griff * 2}
                      height={rahmen.h + griff * 2}
                      fill="none"
                      stroke="#f5f7fa"
                      strokeWidth={Math.max(w, h) * 0.003}
                      strokeDasharray={`${griff} ${griff * 0.7}`}
                    />
                    <circle
                      cx={rahmen.x + rahmen.w + griff}
                      cy={rahmen.y + rahmen.h + griff}
                      r={griff}
                      fill="#f5f7fa"
                      pointerEvents="all"
                      onPointerDown={(e) => {
                        e.stopPropagation();
                        if (!ausgewaehlt) return;
                        anhalten();
                        dispatch({ typ: 'zug-start' });
                        zug.current = {
                          art: 'skalieren',
                          anker: { x: rahmen.x, y: rahmen.y },
                          start: { x: rahmen.x + rahmen.w, y: rahmen.y + rahmen.h },
                          id: ausgewaehlt.id,
                          marke: ausgewaehlt,
                        };
                      }}
                    />
                  </g>
                )}
              </svg>
            )}
          </div>
        )}
      </div>

      {/*
        ⚠️ **Die Steuerung liegt ausserhalb der einklappbaren Leisten.** Vorgabe des Nutzers:
        Start/Weiter, Pause und Sichern bleiben immer sichtbar — sie sind der einzige Weg, eine
        begonnene Bearbeitung zu sichern.
      */}
      <div className={styles.steuerung}>
        <button
          type="button"
          className={`tap ${styles.knopf}`}
          onClick={umschalten}
          disabled={!bereit}
          aria-label={laeuft ? t.videoAnnotate.pause : t.videoAnnotate.play}
        >
          <Icon name={laeuft ? 'pause' : 'play'} size={18} />
        </button>
        {/*
          ⚠️ **Ton aus, bis jemand ihn will.** Der Editor zeigt bewusst keine `controls` — die
          Leiste von iOS läge über der Markierungsebene —, also braucht der Ton hier einen
          eigenen Knopf.

          ⚠️ **Noch mit dem Mikrofonzeichen.** Ein Lautsprecher fehlt im Zeichensatz; welche
          Zeichen es gibt, bestimmt der Nutzer (`docs/icon-zuordnung.html`, s.
          `memory/icon-requirement.md`). Bis dahin trägt der Knopf seine Beschriftung.
        */}
        <button
          type="button"
          className={`tap ${styles.knopf}`}
          onClick={() => setTon((an) => !an)}
          disabled={!bereit}
          aria-label={ton ? t.videoAnnotate.soundOff : t.videoAnnotate.soundOn}
        >
          <Icon name="mic" size={18} />
          <span>{ton ? t.videoAnnotate.soundOff : t.videoAnnotate.soundOn}</span>
        </button>
        <span className={styles.zeit}>{uhr(positionMs)} / {uhr(state.overlay.durationMs)}</span>
        {/*
          ⚠️ **Ein neues Band, auch wo schon eines liegt.** Ohne diesen Knopf zeichnete man in
          ein geltendes Band hinein, und ein zweites, überschneidendes liess sich gar nicht
          anlegen — obwohl das Modell es von Anfang an trägt (`spurFuer`).
        */}
        <button
          type="button"
          className={`tap ${styles.knopf}`}
          onClick={neuesBand}
          disabled={!bereit}
          aria-label={t.videoAnnotate.addBand}
        >
          <Icon name="plus" size={18} />
          <span>{t.videoAnnotate.band}</span>
        </button>
        <button
          type="button"
          className={`tap ${styles.knopf}`}
          onClick={() => {
            if (state.auswahl) dispatch({ typ: 'block-loeschen', id: state.auswahl });
          }}
          disabled={state.auswahl === null}
          aria-label={t.videoAnnotate.deleteBand}
        >
          <Icon name="trash" size={18} />
          <span>{t.videoAnnotate.band}</span>
        </button>
        <span className={styles.luecke} />
        {/* Rechts, unmittelbar vor „Sichern" — Wunsch des Nutzers: die beiden Knöpfe, die
            das Blatt verändern, liegen beieinander, statt einer davon neben der Uhr. */}
        <button
          type="button"
          className={`tap ${styles.knopf}`}
          onClick={() => setOffen((o) => !o)}
          aria-label={offen ? t.videoAnnotate.collapse : t.videoAnnotate.expand}
        >
          {/* Ein eigenes Icon für „aufwärts" wäre dasselbe Zeichen gedreht — die Drehung
              steht deshalb im Stil, nicht in der Zeichentabelle. */}
          <span className={offen ? undefined : styles.gedreht}>
            <Icon name="chevronDown" size={18} />
          </span>
        </button>
        <button type="button" className={`tap ${styles.fertig}`} onClick={schliessen}>
          <Icon name="check" size={17} />
          <span>{t.videoAnnotate.save}</span>
        </button>
      </div>

      {offen && (
        <>
          <Blockleiste
            blocks={state.overlay.blocks}
            auswahl={state.auswahl}
            durationMs={state.overlay.durationMs}
            onWaehlen={waehleBand}
            onRand={(id, rand, ms) => dispatch({ typ: 'block-rand', id, rand, ms })}
            onVerschieben={(id, deltaMs) => dispatch({ typ: 'block-verschieben', id, deltaMs })}
            onZugStart={() => dispatch({ typ: 'zug-start' })}
            onZugEnde={() => dispatch({ typ: 'zug-ende' })}
          />
          <Zeitleiste
            bilder={bilder}
            positionMs={positionMs}
            durationMs={state.overlay.durationMs}
            onSpringen={springe}
            onZugStart={anhalten}
            onZugEnde={() => undefined}
          />
        </>
      )}

      <Toolbar
        werkzeug={werkzeug}
        farbe={farbe}
        stufe={stufe}
        kannUndo={state.verlauf.length > 0}
        kannRedo={state.zukunft.length > 0}
        hatAuswahl={ausgewaehlt !== null}
        groesseWirkt={mitGroesse(ausgewaehlt ?? platzhalter(farbe), stufe) !== null}
        gesperrt={!bereit}
        onWerkzeug={(neu) => {
          // Ein anderes Werkzeug heisst: die begonnene Beschriftung ist nicht mehr gemeint.
          textAbbrechen();
          setWerkzeug(neu);
        }}
        onFarbe={(f) => {
          setFarbe(f);
          if (ausgewaehlt) dispatch({ typ: 'marke-aendern', marke: mitFarbe(ausgewaehlt, f) });
        }}
        onStufe={(neu) => {
          setStufe(neu);
          if (!ausgewaehlt) return;
          const geaendert = mitGroesse(ausgewaehlt, neu);
          if (geaendert) dispatch({ typ: 'marke-aendern', marke: geaendert });
        }}
        onUndo={() => dispatch({ typ: 'undo' })}
        onRedo={() => dispatch({ typ: 'redo' })}
        onMarkeLoeschen={() => {
          if (ausgewaehlt) dispatch({ typ: 'marke-loeschen', id: ausgewaehlt.id });
        }}
      />

      {textFrage && (
        <div className={styles.textFrage}>
          <input
            className={styles.textFeld}
            value={textEingabe}
            onChange={(e) => setTextEingabe(e.target.value)}
            placeholder={t.videoAnnotate.textPlaceholder}
            autoFocus
          />
          <button
            type="button"
            className={`tap ${styles.knopf}`}
            onClick={textAbbrechen}
            aria-label={t.videoAnnotate.cancel}
          >
            <Icon name="close" size={17} />
          </button>
          <button type="button" className={`tap ${styles.knopf}`} onClick={textSetzen}>
            <Icon name="check" size={17} />
          </button>
        </div>
      )}
    </div>
  );
}

/** Eine Marke beim Ziehen mitwachsen lassen — dieselbe Rechnung wie im Foto-Editor. */
function ziehe(marke: Marker, start: Punkt, jetzt: Punkt): Marker {
  switch (marke.kind) {
    case 'arrow':
      return { ...marke, bis: jetzt };
    case 'ellipse':
      return {
        ...marke,
        cx: (start.x + jetzt.x) / 2,
        cy: (start.y + jetzt.y) / 2,
        rx: Math.abs(jetzt.x - start.x) / 2,
        ry: Math.abs(jetzt.y - start.y) / 2,
      };
    case 'rect':
    case 'cover':
      return {
        ...marke,
        x: Math.min(start.x, jetzt.x),
        y: Math.min(start.y, jetzt.y),
        w: Math.abs(jetzt.x - start.x),
        h: Math.abs(jetzt.y - start.y),
      };
    case 'freehand':
      return { ...marke, punkte: [...marke.punkte, jetzt] };
    default:
      return marke;
  }
}

/** Nur zur Frage, ob die Grössenknöpfe überhaupt wirken — s. `ToolbarProps.groesseWirkt`. */
const platzhalter = (farbe: string): Marker => ({
  id: 'x',
  kind: 'arrow',
  color: farbe,
  stroke: 'mittel',
  von: { x: 0, y: 0 },
  bis: { x: 0, y: 0 },
});

/** `m:ss` — im Editor zählt die Stelle im Video, nicht die Uhrzeit. */
function uhr(ms: number): string {
  const ganze = Math.max(0, Math.floor(ms / 1000));
  return Math.floor(ganze / 60) + ':' + String(ganze % 60).padStart(2, '0');
}
